import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Film, ExternalLink, Plus } from "lucide-react";

export default function ClientHome() {
  const { user } = useAuth();
  const { t } = useLang();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/orders", { params: { scope: "active" } }).then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  return (
    <div className="ch-fade-up space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("ch.hello")} {user?.prenom || ""}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("ch.title")}</h1>
        </div>
        <Button asChild className="rounded-sm bg-black hover:bg-neutral-800">
          <Link to="/espace/commander" data-testid="client-order-cta"><Plus className="mr-2 h-4 w-4" /> {t("ch.cta")}</Link>
        </Button>
      </header>

      {orders.length === 0 && (
        <div className="flex flex-col items-center gap-4 rounded-sm border border-dashed border-neutral-200 py-16 text-center">
          <Film className="h-8 w-8 text-neutral-300" strokeWidth={1.5} />
          <p className="text-sm text-neutral-400">{t("ch.empty")}</p>
          <Button asChild variant="outline" className="rounded-sm"><Link to="/espace/commander">{t("ch.discover")}</Link></Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {orders.map((o) => (
          <div key={o.id} data-testid={`client-order-${o.id}`} className="rounded-sm border border-neutral-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-bold">{o.package_name}</h3>
                <p className="text-sm text-neutral-500">{o.seconds_per_video}{t("ao.per_video")}</p>
              </div>
              <StatusBadge status={o.status} />
            </div>

            <div className="mt-5 flex items-center justify-between rounded-sm bg-neutral-50 p-4">
              <span className="text-sm text-neutral-500">{t("ch.remaining_in_pack")}</span>
              <span className="font-display text-2xl font-extrabold">{o.videos_remaining}<span className="text-base text-neutral-400">/{o.video_count}</span></span>
            </div>

            {o.videos.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("ch.delivered_videos")}</p>
                {o.videos.map((v, i) => (
                  <a key={i} href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-sm border border-neutral-100 px-3 py-2 text-sm hover:bg-neutral-50">
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{v.title || v.url}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
